import React, { useEffect, useMemo, useRef, useState } from "react";
import type { PublicCatalogueStatus } from "../../../types/catalogue.types";
import "./catalogueProtection.css";

type Props = {
  status: PublicCatalogueStatus;
  /** Who is looking: the invited buyer's name, or the guest's. */
  viewerName?: string;
};

const escapeXml = (s: string) =>
  s.replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" })[c] as string);

/** One SVG tile, repeated across the page. Lines are kept short so the tile stays legible. */
export function watermarkTile(lines: string[]): string {
  const clean = lines.map((l) => l.trim()).filter(Boolean).map((l) => (l.length > 42 ? `${l.slice(0, 41)}…` : l));
  const text = clean
    .map((l, i) => `<text x="160" y="${100 + (i - (clean.length - 1) / 2) * 18}" text-anchor="middle">${escapeXml(l)}</text>`)
    .join("");
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="200">` +
    `<g transform="rotate(-24 160 100)" fill="#1d1d1f" font-family="-apple-system,Segoe UI,Roboto,sans-serif" font-size="13" font-weight="600">` +
    `${text}</g></svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
}

/**
 * Buyer page protection, from the studio's Access & Security settings.
 * - Visible watermark (optional, off by default): the viewer's name, the date and a link code tiled
 *   over the page. Photos carry a hidden per-link mark from the server whether or not it is on.
 * - Strict: no right-click, image drag, long-press save, copy or print, and the page is covered while
 *   a screenshot shortcut is held or the tab is in the background (app switcher snapshots). A browser
 *   cannot block the phone's own screenshot buttons; the watermark is what makes those traceable.
 */
const CatalogueProtection: React.FC<Props> = ({ status, viewerName }) => {
  const mode = status.screenshotProtection || "off";
  const strict = mode === "strict";
  // The visible watermark is the studio's choice; photos carry a hidden per-link mark either way.
  const showMark = Boolean(status.watermark);
  const [shielded, setShielded] = useState(false);
  const release = useRef<number>();

  const tile = useMemo(() => {
    if (!showMark) return "";
    const date = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    const who = status.studioPreview ? "Studio preview" : viewerName?.trim() || "Private link";
    return watermarkTile([
      status.watermarkText || "",
      `${who} · ${date}`,
      status.watermarkCode ? `#${status.watermarkCode}` : "",
    ]);
  }, [showMark, status.studioPreview, status.watermarkText, status.watermarkCode, viewerName]);

  useEffect(() => {
    if (!strict) return;
    const root = document.documentElement;
    root.classList.add("hkp-protect-strict");

    const shield = (ms?: number) => {
      window.clearTimeout(release.current);
      setShielded(true);
      if (ms) release.current = window.setTimeout(() => setShielded(false), ms);
    };
    const unshield = () => {
      window.clearTimeout(release.current);
      release.current = window.setTimeout(() => setShielded(false), 150);
    };

    const onContext = (e: Event) => {
      const t = e.target as HTMLElement | null;
      // Keep the menu for typing (search, forms).
      if (t?.closest("input, textarea, [contenteditable='true']")) return;
      e.preventDefault();
    };
    const onDrag = (e: DragEvent) => {
      if ((e.target as HTMLElement | null)?.closest("img, picture, video, [role='img']")) e.preventDefault();
    };
    const onCopy = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest("input, textarea")) return;
      e.preventDefault();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      const mod = e.metaKey || e.ctrlKey;
      if (k === "printscreen") {
        shield(1500);
        void navigator.clipboard?.writeText?.("").catch(() => {});
        return;
      }
      // macOS Cmd+Shift+3/4/5, Windows Win+Shift+S.
      if (e.shiftKey && (e.metaKey || (e.getModifierState && e.getModifierState("OS")))) {
        if (["3", "4", "5", "s", "#", "$", "%"].includes(k)) shield(2500);
        else shield();
        return;
      }
      if (mod && (k === "p" || k === "s")) {
        e.preventDefault();
        shield(1200);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "printscreen") void navigator.clipboard?.writeText?.("").catch(() => {});
      if (e.key === "Meta" || e.key === "Shift" || e.key === "OS") unshield();
    };
    const onVisibility = () => (document.visibilityState === "hidden" ? shield() : unshield());
    // A tap always brings the page back (the cover never traps the buyer).
    const onPointer = () => unshield();
    const onBeforePrint = () => shield();
    const onAfterPrint = () => unshield();

    document.addEventListener("contextmenu", onContext);
    document.addEventListener("dragstart", onDrag);
    document.addEventListener("copy", onCopy);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("keyup", onKeyUp, true);
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("pointerdown", onPointer, true);
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      root.classList.remove("hkp-protect-strict");
      window.clearTimeout(release.current);
      document.removeEventListener("contextmenu", onContext);
      document.removeEventListener("dragstart", onDrag);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("keyup", onKeyUp, true);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("pointerdown", onPointer, true);
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, [strict]);

  return (
    <>
      {showMark ? (
        <div className="hkp-protect-mark" style={{ backgroundImage: tile }} aria-hidden="true" data-testid="catalogue-watermark" />
      ) : null}
      {strict && shielded ? (
        <div className="hkp-protect-shield" role="status">
          <span>Protected collection</span>
        </div>
      ) : null}
    </>
  );
};

export default CatalogueProtection;
